import {useLayoutEffect, useMemo} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {RootState} from '../../../../../index';
import {getPlayrateState} from '../../../../../selectors/fortnite-game/playrateSelector';
import {PlayrateState} from '../../../../../reducers/fortnite-game/playrate.reducer';
import playrateActions from '../../../../../actions/fortnite-game/playrate.actions';

const DATA_SET_LENGTH = 20;

export interface UsePlayrateDataParams {
    islandCode: string;
}

export interface UsePlayrateState extends Omit<PlayrateState, 'data' | 'lastFetch'> {
    data: {[key: string]: string[]}[];
    versions: string[];
    reload: (silent: boolean, overwriteCache: boolean) => void;
}

const usePlayrateData = ({islandCode}: UsePlayrateDataParams): UsePlayrateState => {
    const dispatch = useDispatch();

    const {data: allIslandsData = {}, ...rest} = useSelector<RootState, PlayrateState>(
        getPlayrateState,
        shallowEqual
    );

    const islandData = allIslandsData[islandCode] || {};

    const versions = Object.keys(islandData).reverse();

    const formattedData = useMemo(
        () =>
            versions.length
                ? [...Array(DATA_SET_LENGTH).keys()].map(index =>
                      versions.reduce(
                          (acc, version) => ({
                              ...acc,
                              [version]: islandData[version][index]
                          }),
                          {}
                      )
                  )
                : [],
        [allIslandsData, islandCode]
    );

    /*
     * @param silent - Don't toggle loading state
     * @param overwriteCache - Overwrite cached data
     * */
    const fetchData = (silent = false) => {
        dispatch(
            playrateActions.getPlayrate({
                silent
            })
        );
    };

    useLayoutEffect(() => {
        if (islandCode) {
            fetchData(false);
        }
    }, []);

    return {
        data: formattedData,
        versions,
        ...rest,
        reload: () => fetchData(false)
    };
};

export default usePlayrateData;
